import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { api, ApiError } from '../../lib/api';

export default function ReconciliationPage() {
  const { t } = useTranslation('admin');
  const { currentEntity } = useEntity();
  const { walletId } = useParams<{ walletId: string }>();

  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);

  const { data, isLoading, error } = useQuery({
    queryKey: ['wallets', 'reconciliation', walletId, from, to],
    queryFn: () => api.admin.wallets.onchainTransactions(currentEntity!.id, walletId!, from, to),
    enabled: !!currentEntity && !!walletId,
    staleTime: 0,
    gcTime: 0,
  });

  const result = data?.data;
  const matched = result?.matched || [];
  const onchainOnly = result?.onchainOnly || [];
  const systemOnly = result?.systemOnly || [];

  const formatDt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Madrid' })
      + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
  };

  const truncAddr = (addr: string) =>
    addr.length > 12 ? addr.slice(0, 6) + '...' + addr.slice(-4) : addr;

  const explorerLink = (txHash: string) => {
    const href = result?.network === 'ethereum'
      ? `https://etherscan.io/tx/${txHash}`
      : `https://tronscan.org/#/transaction/${txHash}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary"
        title={txHash}
      >
        {txHash.slice(0, 10)}...
      </a>
    );
  };

  const matchedSum = matched.reduce((sum, m) => {
    const amt = parseFloat(m.onchain.amount);
    return sum + (m.onchain.direction === 'in' ? amt : -amt);
  }, 0);

  const onchainOnlySum = onchainOnly.reduce((sum, tx) => {
    const amt = parseFloat(tx.amount);
    return sum + (tx.direction === 'in' ? amt : -amt);
  }, 0);

  const systemOnlySum = systemOnly.reduce((sum, mv) => sum + parseFloat(mv.amount), 0);

  const fmtSum = (v: number) => (v >= 0 ? '+' : '') + v.toFixed(2);

  if (!currentEntity) return null;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Link to="/admin/wallets" className="btn btn-secondary px-3 py-1">
            &larr; {t('wallets.title', 'Кошельки')}
          </Link>
          <h1 className="page-title">
            {t('wallets.reconciliation.title', 'Сверка транзакций')}
            {result && <span className="text-muted font-normal"> — {result.walletName}</span>}
          </h1>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4 p-4">
        <div className="flex gap-4 items-end flex-wrap">
          <div className="form-group mb-0">
            <label className="form-label text-xs">{t('wallets.reconciliation.from', 'С')}</label>
            <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="form-group mb-0">
            <label className="form-label text-xs">{t('wallets.reconciliation.to', 'По')}</label>
            <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {result && (
            <div className="flex gap-2 items-center text-xs font-mono">
              <span className="badge badge-closed">{result.network.toUpperCase()}</span>
              <span className="text-muted">{result.address}</span>
            </div>
          )}
          {!isLoading && !error && (
            <div className="flex gap-4 ml-auto text-sm">
              <span className="text-success font-semibold">{t('wallets.reconciliation.matched', 'Совпали')}: {matched.length}</span>
              <span className="text-error font-semibold">{t('wallets.reconciliation.onchainOnly', 'Только блокчейн')}: {onchainOnly.length}</span>
              <span className="font-semibold" style={{ color: 'var(--ctp-peach)' }}>{t('wallets.reconciliation.systemOnly', 'Только система')}: {systemOnly.length}</span>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="spinner mb-3" />
          <p className="text-muted">{t('wallets.reconciliation.loading', 'Загрузка транзакций...')}</p>
        </div>
      ) : error ? (
        <div className="card p-6 text-center text-error">
          {(error as ApiError).message || 'Error'}
        </div>
      ) : (
        <>
          {/* Matched pairs */}
          {matched.length > 0 && (
            <div className="card mb-4">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-success">
                  {t('wallets.reconciliation.matched', 'Совпали')} ({matched.length})
                </h2>
                <span className={`font-mono font-semibold ${matchedSum >= 0 ? 'text-success' : 'text-error'}`}>
                  {fmtSum(matchedSum)} {result?.currencyCode}
                </span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('wallets.reconciliation.date', 'Дата')} (блокчейн)</th>
                    <th>{t('wallets.reconciliation.date', 'Дата')} (система)</th>
                    <th>{t('wallets.reconciliation.amount', 'Сумма')}</th>
                    <th>{t('wallets.reconciliation.counterparty', 'Контрагент')}</th>
                    <th>TX</th>
                    <th>{t('wallets.reconciliation.type', 'Тип')}</th>
                    <th>{t('wallets.reconciliation.match', 'Совпадение')}</th>
                    <th>&Delta;t</th>
                  </tr>
                </thead>
                <tbody>
                  {matched.map((m) => {
                    const mv = m.system;
                    const byHash = m.matchedBy === 'hash';
                    return (
                      <tr key={m.onchain.txHash}>
                        <td className="whitespace-nowrap">{formatDt(m.onchain.timestamp)}</td>
                        <td className="whitespace-nowrap">{formatDt(m.system.createdAt)}</td>
                        <td className={`font-mono font-semibold ${m.onchain.direction === 'in' ? 'text-success' : 'text-error'}`}>
                          {m.onchain.direction === 'in' ? '+' : '-'}{parseFloat(m.onchain.amount).toFixed(2)}
                        </td>
                        <td className="font-mono text-muted" title={m.onchain.counterparty}>
                          {truncAddr(m.onchain.counterparty)}
                        </td>
                        <td>{explorerLink(m.onchain.txHash)}</td>
                        <td className="text-muted">
                          {mv.debtPaymentId ? 'debt' : mv.transactionId ? 'tx' : 'manual'}
                        </td>
                        <td>
                          {byHash ? (
                            <span
                              className="badge"
                              style={{ background: 'var(--ctp-teal)22', color: 'var(--ctp-teal)', fontWeight: 600 }}
                              title={t('wallets.reconciliation.matchByHashTitle', 'Точное совпадение tx hash')}
                            >
                              ✓ hash
                            </span>
                          ) : (
                            <span
                              className="badge"
                              style={{ background: 'var(--ctp-yellow)22', color: 'var(--ctp-yellow)', fontWeight: 500 }}
                              title={t('wallets.reconciliation.matchByHeuristicTitle', 'Эвристическое совпадение по сумме и времени')}
                            >
                              ~ heuristic
                            </span>
                          )}
                        </td>
                        <td className="text-muted">{m.timeDiffMinutes}m</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Onchain only */}
          {onchainOnly.length > 0 && (
            <div className="card mb-4">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-error">
                  {t('wallets.reconciliation.onchainOnly', 'Только в блокчейне')} ({onchainOnly.length})
                </h2>
                <span className={`font-mono font-semibold ${onchainOnlySum >= 0 ? 'text-success' : 'text-error'}`}>
                  {fmtSum(onchainOnlySum)} {result?.currencyCode}
                </span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('wallets.reconciliation.date', 'Дата')}</th>
                    <th>{t('wallets.reconciliation.amount', 'Сумма')}</th>
                    <th>{t('wallets.reconciliation.counterparty', 'Контрагент')}</th>
                    <th>TX</th>
                  </tr>
                </thead>
                <tbody>
                  {onchainOnly.map((tx) => (
                    <tr key={tx.txHash}>
                      <td className="whitespace-nowrap">{formatDt(tx.timestamp)}</td>
                      <td className={`font-mono font-semibold ${tx.direction === 'in' ? 'text-success' : 'text-error'}`}>
                        {tx.direction === 'in' ? '+' : '-'}{parseFloat(tx.amount).toFixed(2)}
                      </td>
                      <td className="font-mono text-muted" title={tx.counterparty}>
                        {truncAddr(tx.counterparty)}
                      </td>
                      <td>{explorerLink(tx.txHash)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* System only */}
          {systemOnly.length > 0 && (
            <div className="card mb-4">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold" style={{ color: 'var(--ctp-peach)' }}>
                  {t('wallets.reconciliation.systemOnly', 'Только в системе')} ({systemOnly.length})
                </h2>
                <span className={`font-mono font-semibold ${systemOnlySum >= 0 ? 'text-success' : 'text-error'}`}>
                  {fmtSum(systemOnlySum)} {result?.currencyCode}
                </span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('wallets.reconciliation.date', 'Дата')}</th>
                    <th>{t('wallets.reconciliation.amount', 'Сумма')}</th>
                    <th>{t('wallets.reconciliation.type', 'Тип')}</th>
                  </tr>
                </thead>
                <tbody>
                  {systemOnly.map((mv) => {
                    const amt = parseFloat(mv.amount);
                    return (
                      <tr key={mv.id}>
                        <td className="whitespace-nowrap">{formatDt(mv.createdAt)}</td>
                        <td className={`font-mono font-semibold ${amt >= 0 ? 'text-success' : 'text-error'}`}>
                          {amt >= 0 ? '+' : ''}{amt.toFixed(2)}
                        </td>
                        <td className="text-muted">
                          {mv.debtPaymentId ? 'debt' : mv.transactionId ? 'tx' : 'manual'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {matched.length === 0 && onchainOnly.length === 0 && systemOnly.length === 0 && (
            <div className="card p-8 text-center text-muted">
              {t('wallets.reconciliation.noTransactions', 'Нет транзакций')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
